'use client';

import {
  Button,
  Input,
  Menu,
  MenuContent,
  MenuDivider,
  MenuItem,
  MenuSection,
  MenuTrigger,
} from '@lulalife/design-system';
import { ChevronDown, List } from '@lulalife/design-system/icons/line';
import { useState } from 'react';
import { MenuScrollRegion } from './MenuScrollRegion';

// DS-GAP(menu-item-selected-focus): the menu focuses its first item on open, and DS MenuItem's
// `focus-visible` hover fill (#f1f5f9) then covers its own selected fill. Figma's selected Menu / Item
// is `menu/item/bg-active` (#edf4fe), so a selected row keeps that fill while focused; the focus
// outline still shows. (Its Medium label is the DS `selected` state already.)
export const SELECTED_ITEM_CLASS_NAME = '[&_.menu-item:focus-visible]:bg-menu-item-bg-active';

/** Table / Filters / Menu type=Views width. */
const PANEL_WIDTH = 240;
const MAX_VIEW_NAME_LENGTH = 20;

export type ListViewOption = { id: string; label: string; isDefault: boolean };

type ListViewsMenuProps = {
  views: ReadonlyArray<ListViewOption>;
  currentId: string;
  /** The current view has changes that aren't saved (the Views Button's State=Changed). */
  hasChanges: boolean;
  canSaveNewView: boolean;
  isSaving: boolean;
  onSelect: (id: string) => void;
  /** Resolves true once the view is saved. */
  onSaveNewView: (name: string) => Promise<boolean>;
  /**
   * The list's frame: Service Requests (7633:181182) has "VIEWS", a divider, "(Default)" and a
   * full-width Save a New View button; Work Orders (7506:10909) has "Views", no "(Default)" and
   * Save a New View as a plain menu row.
   */
  variant?: 'service-requests' | 'work-orders';
};

/**
 * The Views button and menu (Service Requests 7633:181182, Work Orders 7506:10909): the views, the
 * current one highlighted, then Save a New View, which asks for the new view's name.
 */
export const ListViewsMenu = ({
  views,
  currentId,
  hasChanges,
  canSaveNewView,
  isSaving,
  onSelect,
  onSaveNewView,
  variant = 'service-requests',
}: ListViewsMenuProps) => {
  const isWorkOrders = variant === 'work-orders';
  const [isOpen, setIsOpen] = useState(false);
  const [newName, setNewName] = useState<string | null>(null);
  const current = views.find((view) => view.id === currentId);

  const setOpen = (open: boolean) => {
    setIsOpen(open);
    if (!open) setNewName(null);
  };

  const save = async () => {
    const name = newName?.trim() ?? '';
    if (!name || isSaving) return;
    if (await onSaveNewView(name)) setOpen(false);
  };

  if (views.length === 0) return null;

  return (
    <Menu open={isOpen} onOpenChange={setOpen}>
      <MenuTrigger>
        <Button
          type="button"
          variant="secondary"
          leftIcon={<List aria-hidden="true" />}
          rightIcon={<ChevronDown aria-hidden="true" />}
          showBadge={hasChanges}
          badgeDot
          data-testid="view-select-dropdown"
        >
          {current?.label ?? 'Views'}
        </Button>
      </MenuTrigger>
      <MenuContent data-lula-ds="" align="end" width={PANEL_WIDTH} aria-label="Views">
        <MenuSection label={isWorkOrders ? 'Views' : 'VIEWS'} />
        {isWorkOrders ? null : <MenuDivider />}
        <MenuScrollRegion>
          {views.map((view) => (
            <MenuItem
              key={view.id}
              label={view.label}
              rightText={view.isDefault && !isWorkOrders ? '(Default)' : undefined}
              selected={view.id === currentId}
              className={view.id === currentId ? SELECTED_ITEM_CLASS_NAME : undefined}
              onClick={() => {
                setOpen(false);
                if (view.id !== currentId) onSelect(view.id);
              }}
            />
          ))}
        </MenuScrollRegion>
        {canSaveNewView ? (
          <>
            <MenuDivider />
            {isWorkOrders && newName === null ? (
              <div data-testid="view-save-new">
                <MenuItem label="Save a New View" onClick={() => setNewName('')} />
              </div>
            ) : (
              <div className="flex flex-col gap-space-8 px-space-8 pt-space-4">
                {newName === null ? (
                  <Button
                    type="button"
                    variant="secondary"
                    block
                    onClick={() => setNewName('')}
                    data-testid="view-save-new"
                  >
                    Save a New View
                  </Button>
                ) : (
                  <>
                    <Input
                      aria-label="View name"
                      placeholder="Type view name"
                      value={newName}
                      maxLength={MAX_VIEW_NAME_LENGTH}
                      autoFocus
                      onChange={(event) => setNewName(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') save();
                      }}
                      data-testid="view-name-input-new"
                    />
                    <div className="flex gap-space-8">
                      <Button
                        type="button"
                        variant="secondary"
                        block
                        disabled={isSaving}
                        onClick={() => setNewName(null)}
                        data-testid="view-form-discard"
                      >
                        Discard
                      </Button>
                      <Button
                        type="button"
                        variant="primary"
                        block
                        isLoading={isSaving}
                        disabled={isSaving || newName.trim() === ''}
                        onClick={save}
                        data-testid="view-form-save"
                      >
                        Save
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        ) : null}
      </MenuContent>
    </Menu>
  );
};
